import { Button } from "flowbite-react";
import PropTypes from "prop-types";
import React, { Fragment, useCallback, useEffect, useMemo, useState, useContext, useRef } from "react";
import { IoIosTrash } from "react-icons/io";
import { connect } from "react-redux";
import { useReactFlow } from "reactflow";

import { Menu, Transition } from "@headlessui/react";
import ConfirmDialog from "../../../components/confirm-dialog/confirm-dialog";
import { customAddRowTheme } from "../../../components/dynamic-rows";
import Message from "../../../components/toast-message";
import { classNames } from "../../../helpers/utils/css-utils";
import { stmSettingSave } from "../../../redux/journey-toolbox/journey-toolbox-actions";
import journeyStmStore from "../common/service/journey-stm-store";
import { settingsTypes } from "./settings-types-registry";
import { WorkflowCanvasContext } from "../../../contexts/WorkflowCanvasContextProvider";
import { HiOutlineColorSwatch } from "react-icons/hi";
import IntentPrompt from "../common/components/intent-prompt";

const ChildNode = React.memo((props) => {
  // Use a ref to keep track of the latest intentPrompt to inject it when child calls onChangeData
  const intentPromptRef = useRef(props.savedSettings?.intentPrompt || "");

  useEffect(() => {
    if (props.savedSettings?.intentPrompt !== undefined) {
      intentPromptRef.current = props.savedSettings.intentPrompt;
    }
  }, [props.savedSettings]);

  const handleChildChangeData = useCallback((newData) => {
    props.onChangeData({
      ...newData,
      intentPrompt: intentPromptRef.current
    });
  }, [props.onChangeData]);

  const handleIntentChange = useCallback((e) => {
    const value = e.target.value;
    intentPromptRef.current = value;
    props.onChangeData({
      ...props.savedSettings,
      intentPrompt: value
    });
  }, [props.onChangeData, props.savedSettings]);

  const childrenWithProps = React.Children.map(props.children, (child) => {
    const childProps = {
      id: props.id,
      name: props.name,
      onChangeData: handleChildChangeData,
      savedSettings: props.savedSettings,
      metadata: props.metadata,
      closeSettingPage: props.closeSettingPage,
    };

    if (React.isValidElement(child)) {
      return React.cloneElement(child, childProps);
    }

    return child;
  });

  return (
    <div className="flex flex-col gap-4 pb-4">
      <div>{childrenWithProps}</div>
      <div className="pt-4">
        <IntentPrompt
          value={props.savedSettings?.intentPrompt || ""}
          onChange={handleIntentChange}
        />
      </div>
    </div>
  );
});

function Settings({ closeSettingPage }) {
  const workflowCanvasContext = useContext(WorkflowCanvasContext);
  const nodeAttribute = workflowCanvasContext.nodeAttribute
  const metadata = workflowCanvasContext.metadata
  const { id, nodeType, label: name } = nodeAttribute

  const [dialogSettings, setDialogSettings] = useState({
    open: false,
    message: null,
  });
  const { deleteElements } = useReactFlow();

  const [settingMenuExpand, setSettingMenuExpand] = useState(false);
  const [colorPickerOpen, setColorPickerOpen] = useState(false);

  // Predefined color options
  const colorOptions = [
    { name: 'White', value: null },
    { name: 'Red Light', value: '#FCA5A5' },
    { name: 'Orange Light', value: '#FCD59F' },
    { name: 'Yellow Light', value: '#FDE68A' },
    { name: 'Green Light', value: '#6EE7B7' },
    { name: 'Teal Light', value: '#99F6E4' },
    { name: 'Blue Light', value: '#BFDBFE' },
    { name: 'Indigo Light', value: '#C7D2FE' },
    { name: 'Purple Light', value: '#DDD6FE' },
    { name: 'Gray Light', value: '#D1D5DB' },
    { name: 'Pink Light', value: '#FBCFE8' },
    { name: 'Rose Light', value: '#FECACA' },
    { name: 'Amber Light', value: '#FCD34D' },
    { name: 'Lime Light', value: '#D9F99D' },
    { name: 'Emerald Light', value: '#A7F3D0' },
    { name: 'Cyan Light', value: '#A5F3FC' },
    { name: 'Sky Blue Light', value: '#BAE6FD' },
    { name: 'Violet Light', value: '#E9D5FF' },
    { name: 'Slate Light', value: '#E2E8F0' },
    { name: 'Zinc Light', value: '#E4E4E7' }
  ];

  // Get the current color from stateConfig or use default
  const currentColor = nodeAttribute.stateConfig?.color || colorOptions[0].value;

  let content;
  let title = "";

  const settingsType = settingsTypes[nodeType];

  if (settingsType) {
    title = settingsType.title;
    content = settingsType.component;
  }

  const onChangeData = useCallback((data) => {
    workflowCanvasContext.handleNodeFullConfigChange(data)
  }, []);

  const onClickDelete = (nodeTitle) => {
    setDialogSettings({
      open: true,
      message: `Are you sure you want to delete "${nodeTitle}"`,
    })
  }

  const onDeleteConfirm = (yes) => {
    if (yes) {
      workflowCanvasContext.handleDelete(nodeAttribute.parentId, nodeAttribute.id)
    }
    setDialogSettings((preSettings) => ({
      ...preSettings,
      open: false,
    }));
    closeSettingPage();
  }

  const handleColorSelect = (colorValue) => {
    // Update the stateConfig with the selected color
    const updatedConfig = {
      ...nodeAttribute.stateConfig,
      color: colorValue
    };

    // Call onChangeData to update the state
    onChangeData(updatedConfig);

    // Close the color picker
    setColorPickerOpen(false);
  };

  const [copySuccess, setCopySuccess] = useState();

  const copyToClipboard = async e => {
    if ('clipboard' in navigator) {
      await navigator.clipboard.writeText(id);
    } else {
      document.execCommand('copy', true, id);
    }
    setCopySuccess(true);
  };

  useEffect(() => {
    if (copySuccess) {
      setTimeout(() => {
        setCopySuccess(false);
      }, 2000)
    }
  }, [copySuccess]);


  const memoizedMetadata = useMemo(() => metadata, [metadata.journeyId, metadata.slots]);

  const options = [
    {
      name: 'Copy Step Id',
      onClick: () => { copyToClipboard() }
    }
  ]

  return (
    <>
      <div className="p-0 box-border scrollbars h-[calc(100vh-4rem)] overflow-hidden overflow-y-auto">
        <div className="p-3 pr-4 pl-5 bg-slate-100 border-t border-b dark:bg-gray-800 sticky top-0 border-gray-200 dark:border-gray-700 z-10">
          <div className="flex justify-between">
            <p className="text-lg font-semibold text-gray-900 dark:text-white">
              {title}
            </p>
            <div className="flex justify-between gap-2" >
              {/* Color Picker Button */}
              <div className="relative">
                <Button
                  size="xs"
                  theme={customAddRowTheme}
                  color="gray"
                  onClick={() => setColorPickerOpen(!colorPickerOpen)}
                >
                  <div className="flex items-center">
                    <div
                      className="w-4 h-4 rounded-full mr-1"
                      style={{ backgroundColor: currentColor }}
                    ></div>
                    <HiOutlineColorSwatch className="h-6 w-6" />
                  </div>
                </Button>

                {/* Color Picker Dropdown */}
                {colorPickerOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-20 border border-gray-200">
                    <div className="p-2 grid grid-cols-5 gap-2">
                      {colorOptions.map((color) => (
                        <button
                          key={color.value}
                          onClick={() => handleColorSelect(color.value)}
                          className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center"
                          style={{ backgroundColor: color.value }}
                          title={color.name}
                        >
                          {currentColor === color.value && (
                            <span className="text-white text-xs">✓</span>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <Button size="xs" theme={customAddRowTheme} color="gray" onClick={() => onClickDelete(title)}>
                <IoIosTrash className="h-6 w-6" />
              </Button>

              <div className="">
                <Menu as="div" className="">
                  <div>
                    <Menu.Button
                      className="flex items-center text-center font-medium focus:z-10 focus:outline-none text-gray-900 bg-white border border-gray-200 enabled:hover:bg-gray-100 enabled:hover:text-cyan-700 :ring-cyan-700 focus:text-cyan-700 dark:bg-transparent dark:text-gray-400 dark:border-gray-600 dark:enabled:hover:text-white dark:enabled:hover:bg-gray-700 rounded-lg focus:ring-2">
                      <span className="sr-only">Open options</span>
                      <svg xmlns="http://www.w3.org/2000/svg"
                        fill="none" viewBox="0 0 24 24"
                        strokeWidth={1.5} stroke="currentColor"
                        className="w-7 h-7">
                        <path strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M12 6.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 12.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 18.75a.75.75 0 110-1.5.75.75 0 010 1.5z" />
                      </svg>
                    </Menu.Button>
                  </div>

                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items
                      className="absolute z-50 right-0 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                      <div className="py-1">
                        {options.map((option, oi) =>
                          <Menu.Item key={oi}>
                            {({ active }) => (
                              <a
                                href="#"
                                className={classNames(
                                  active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                                  'block px-4 py-2 text-sm'
                                )}
                                onClick={() => {
                                  option.onClick();
                                }}
                              >
                                {option.name}
                              </a>
                            )}
                          </Menu.Item>
                        )}
                      </div>
                    </Menu.Items>
                  </Transition>
                </Menu>
              </div>
            </div>
          </div>
        </div>
        {/* Setting options */}
        {content ? (
          <div className="p-5 block">
            <ChildNode
              key={id}
              id={id}
              name={name}
              metadata={memoizedMetadata}
              savedSettings={nodeAttribute.stateConfig}
              onChangeData={onChangeData}
              closeSettingPage={closeSettingPage}
            >
              {content}
            </ChildNode>
          </div>
        ) : (
          <div
            className="
          h-[90%] text-sm text-gray-300 dark:text-gray-500
          flex flex-1 flex-grow justify-center items-center"
          >
            No settings in {title}
          </div>
        )}

        <div className="flex flex-row justify-center items-center">
          {copySuccess &&
            <Message text={`Copied ${id}`} type="success" />
          }
        </div>
      </div>
      <ConfirmDialog
        onCompletion={onDeleteConfirm}
        {...dialogSettings}
      />
    </>
  );
}

const mapsStateToProps = (state, ownProps) => {
  return {};
};

const mapDispatchToProps = (dispatch) => {
  return {
    stmSettingSaveHandle: (data) => {
      dispatch(stmSettingSave(data));
    },
  };
};

Settings.propTypes = {
  selectedNode: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    nodeIndex: PropTypes.number,
    type: PropTypes.string,
  }),
  metadata: PropTypes.shape({
    journeyId: PropTypes.string,
    slots: PropTypes.object,
  }),
};

export default connect(mapsStateToProps, mapDispatchToProps)(Settings);
